import { FC, useEffect, useState } from 'react'
import cn from 'classnames'
import { Select as AntdSelect } from 'antd'
import CrmSelect from '../../base/Select/Select'
import { TrafficChannel, TrafficChannelShort } from '@/api/types/crm/crm.types'
import { crmTrafficChannelApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import classes from './index.module.css'
import { useLeadRefetch } from '@pages/Admin/CRM/components/LeadProfileModal/LeadRefetchContext'
import LeadProfileModalBlock from '@pages/Admin/CRM/components/LeadProfileModal/components/LeadProfileModalBlock/LeadProfileModalBlock'

export type TrafficChannelLayout = 'table' | 'panel'

interface TrafficChannelBlockProps {
  leadId: number
  current: TrafficChannelShort | null
  channels: TrafficChannel[]
  canEdit: boolean
  /** Канбан: после успешной смены канала */
  onKanbanCardSync?: () => void
  /** `table` — ячейка таблицы воронки; `panel` — карточка в модалке */
  layout?: TrafficChannelLayout
  /** Доп. класс на корень (например внешние отступы) */
  wrapperClassName?: string
  /**
   * Вместе с `onDraftTrafficChannelIdChange` — без немедленного API;
   * значение селекта из `draftTrafficChannelId`, сохранение на стороне родителя.
   */
  draftTrafficChannelId?: number | null
  onDraftTrafficChannelIdChange?: (channelId: number | null) => void
}

const TrafficChannelBlock: FC<TrafficChannelBlockProps> = ({
  leadId,
  current,
  channels,
  canEdit,
  onKanbanCardSync,
  layout = 'table',
  wrapperClassName,
  draftTrafficChannelId,
  onDraftTrafficChannelIdChange,
}) => {
  const isDraftMode = onDraftTrafficChannelIdChange != null

  const [localValue, setLocalValue] = useState<number | null>(
    current?.id ?? null,
  )
  const [loading, setLoading] = useState(false)
  const addAlert = useAddAlert()

  useEffect(() => {
    if (!isDraftMode) {
      setLocalValue(current?.id ?? null)
    }
  }, [current?.id, isDraftMode])

  const effectiveChannelId = isDraftMode
    ? draftTrafficChannelId ?? null
    : localValue

  const selected = channels.find((c) => c.id === effectiveChannelId)
  const { handleRefetchLeadInModal, handleRefetchLeadsOnClose } =
    useLeadRefetch()

  const handleSelectChange = async (channelId: number) => {
    if (isDraftMode) {
      onDraftTrafficChannelIdChange?.(channelId)
      return
    }
    setLoading(true)
    try {
      await crmTrafficChannelApi.updateLeadChannel(leadId, channelId)
      setLocalValue(channelId)
      addAlert({ text: 'Канал трафика обновлён', type: 'success' })
      handleRefetchLeadInModal()
      handleRefetchLeadsOnClose()
      onKanbanCardSync?.()
    } catch {
      addAlert({ text: 'Ошибка обновления канала', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const isPanel = layout === 'panel'

  const channelOptions = channels.map((c) => ({
    value: c.id,
    label: (
      <div className={classes.option}>
        <div className={classes.dot} style={{ backgroundColor: c.color }} />
        {c.name}
      </div>
    ),
  }))

  const control = canEdit ? (
    isPanel ? (
      <CrmSelect
        value={effectiveChannelId ?? undefined}
        onChange={handleSelectChange}
        loading={!isDraftMode && loading}
        placeholder="Не выбрано"
        size="small"
        className={cn(classes.select, classes.selectInPanel)}
        options={channelOptions}
      />
    ) : (
      <AntdSelect
        value={effectiveChannelId ?? undefined}
        onChange={handleSelectChange}
        loading={!isDraftMode && loading}
        placeholder="Не выбрано"
        size="middle"
        className={classes.select}
        options={channelOptions}
      />
    )
  ) : (
    <div className={classes.readOnly}>
      {selected ? (
        <div className={classes.option}>
          <div
            className={classes.dot}
            style={{ backgroundColor: selected.color }}
          />
          {selected.name}
        </div>
      ) : (
        <span className={classes.empty}>Канал не выбран</span>
      )}
    </div>
  )

  if (isPanel) {
    return (
      <div
        className={cn(classes.root, classes.panelShell, wrapperClassName)}
      >
        <LeadProfileModalBlock
          title="Канал трафика"
          titleColor="#15803d"
          accentColor="rgb(108, 206, 103)"
          aria-label="Канал трафика"
          compact
          className={classes.panelBlock}
        >
          {control}
        </LeadProfileModalBlock>
      </div>
    )
  }

  return (
    <div
      className={cn(
        classes.root,
        classes.table,
        wrapperClassName,
      )}
    >
      <>
        <p className={classes.label}>Канал трафика</p>
        {control}
      </>
    </div>
  )
}

export default TrafficChannelBlock
